export interface UserRank {
  key: 'explorer' | 'sage' | 'scholar' | 'curator' | 'maestro';
  title: string;
  emoji: string;
  color: string;
  tagline: string;
  minObjects: number;
  maxObjects: number | null;
  perks: string[];
  shareCopy: string;
}

export const RANK_TIERS: UserRank[] = [
  {
    key: 'explorer',
    title: 'Explorer',
    emoji: '🔍',
    color: '#8B8B8B',
    tagline: 'Beginning your journey of discovery',
    minObjects: 0,
    maxObjects: 0,
    perks: [],
    shareCopy: 'I just started my museum journey on COMENIUS! 🔍',
  },
  {
    key: 'sage',
    title: 'Sage',
    emoji: '📖',
    color: '#FAA31B',
    tagline: 'Collecting wisdom, one experience at a time',
    minObjects: 1,
    maxObjects: 3,
    perks: ['Early access to beta tours'],
    shareCopy: 'I\'m a Sage on COMENIUS — collecting wisdom! 📖',
  },
  {
    key: 'scholar',
    title: 'Scholar',
    emoji: '🎓',
    color: '#4A9EFF',
    tagline: 'Building deep knowledge across museums',
    minObjects: 4,
    maxObjects: 10,
    perks: ['Early access to beta tours', 'Custom profile badge'],
    shareCopy: 'I\'m a Scholar on COMENIUS — knowledge is my passion! 🎓',
  },
  {
    key: 'curator',
    title: 'Curator',
    emoji: '🏛️',
    color: '#9B59B6',
    tagline: 'Guardian of hidden histories',
    minObjects: 11,
    maxObjects: 30,
    perks: ['Early access to beta tours', 'Custom profile badge', 'Exclusive Curator rewards', 'Priority support'],
    shareCopy: 'I\'m a Curator on COMENIUS — guardian of hidden histories! 🏛️',
  },
  {
    key: 'maestro',
    title: 'Maestro',
    emoji: '🎼',
    color: '#FF6B35',
    tagline: 'Orchestrating a legendary journey',
    minObjects: 31,
    maxObjects: null,
    perks: ['All Curator perks', 'Co-create museum content', 'Lifetime access badge'],
    shareCopy: 'I\'m a Maestro on COMENIUS — orchestrating a legendary journey! 🎼',
  },
];

/**
 * Calculates visitor rank based on the number of unique exhibitions discovered.
 *
 * Thresholds reflect a meaningful museum visit journey:
 *   Explorer    → 0  (hasn't started yet)
 *   Sage        → 1–3
 *   Scholar     → 4–10
 *   Curator     → 11–30
 *   Maestro     → 31+
 */
export const getRank = (objectsDiscovered: number): UserRank => {
  if (objectsDiscovered <= 0)  return RANK_TIERS[0]; // Explorer
  if (objectsDiscovered <= 3)  return RANK_TIERS[1]; // Sage
  if (objectsDiscovered <= 10) return RANK_TIERS[2]; // Scholar
  if (objectsDiscovered <= 30) return RANK_TIERS[3]; // Curator
  return RANK_TIERS[4];                              // Maestro
};

/** Returns the next rank tier after `current`, or null if already Maestro. */
export const getNextRank = (current: UserRank): UserRank | null => {
  const idx = RANK_TIERS.findIndex(t => t.key === current.key);
  return idx >= 0 && idx < RANK_TIERS.length - 1 ? RANK_TIERS[idx + 1] : null;
};

/** Returns 0–100 progress percentage and number of experiences remaining. */
export const getRankProgress = (current: UserRank, objectsDiscovered: number): { pct: number; remaining: number } => {
  if (current.maxObjects === null) return { pct: 100, remaining: 0 }; // Maestro
  const span = current.maxObjects - current.minObjects + 1;
  const done = objectsDiscovered - current.minObjects;
  const pct = Math.min(100, Math.max(0, Math.round((done / span) * 100)));
  const remaining = Math.max(0, current.maxObjects + 1 - objectsDiscovered);
  return { pct, remaining };
};
